/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { AppBar, Button, Drawer, Hidden, IconButton, Theme, Toolbar, Typography } from "@material-ui/core";
import HelpIcon from "@material-ui/icons/Help";
import MenuIcon from "@material-ui/icons/Menu";
import { createStyles, makeStyles } from "@material-ui/styles";
import { History } from "history";
import { has } from "json-pointer";
import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps, useRouteMatch, Route } from "react-router";
import { Link } from "react-router-dom";
import { AppState } from "../../redux";
import NavLinks from "./NavLinks";

export const drawerWidth = "240px";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    links: {
      display: "flex",
    },
    navLinks: {
      position: "absolute",
      bottom: 0,
      marginLeft: "86px",
      height: "40px",
      display: "flex",
    },
    header: {
      float: "none",
      height: "60px",
      display: "block",
      overflow: "visible",
    },
    toolbar: {
      height: "inherit",
    },
    logo: {
      position: "relative",
      maxHeight: "72px",
      marginTop: theme.spacing(4),
      display: "inline-block",
      zIndex: 1000,

    },
    helpButton: {
      color: "white"
    },
    drawer: {
      flexBasis: drawerWidth,
      zIndex: 0,
      height: "100%",
    },
    drawerHeader: {},
    greeting: {
      color: theme.palette.primary.main,
      margin: theme.spacing(1),
      textAlign: "center",
    },
    greetingText: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    menuButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    link: {
      display: "flex",
      height: "40px",
      marginLeft: theme.spacing(2),
    },
    linkText: {
      textDecoration: "none", color: "inherit",
    },
    paper: {
      backgroundColor: theme.palette.primary.main,
      position: "relative",
      top: "inherit",
      left: "inherit",
      bottom: 0,
      padding: 0,
      width: drawerWidth,
      zIndex: 1,
      borderRight: 0,
    },
    drawerRoot: {
      display: "flex",
      height: "100%",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    drawerContent: {
      flex: 1,
      overflow: "scroll",
    },
    drawerFooter: {
      display: "flex",
      flexDirection: "column",
    },
    buttonLink: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      textTransform: "none",
      justifyContent: "flex-start",
    },
    buttonLinkText: {
      textDecoration: "none",
      marginLeft: theme.spacing(2),
    },
    signOutButtonFooter: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
    },
    version: {
      position: "absolute",
      top: theme.spacing(1),
      left: theme.spacing(1),
    },
  })
);

interface AppHeaderProps extends RouteComponentProps {
  userId: string;
  onSignOut: () => void;
  history: History<any>;
}

const AppHeader: FC<AppHeaderProps> = props => {
  const classes = useStyles({});
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const evalID = useRouteMatch("/evals/:evalId");
  const followUpID = useRouteMatch("/follow-ups/:facilityId/:planId");

  const name = useSelector((state: AppState) => {
    if (!has(state, "/session/dfdmToken/profile/name")) return undefined;

    return state.session.dfdmToken!.profile.name;
  });

  const signOutButton = (
    <Button className={classes.menuButton} onClick={props.onSignOut}>
      <Typography variant="body2" color="inherit">
        Sign out
      </Typography>
    </Button>
  );

  const signInButton = (
    <Link to="/login" className={classes.linkText}>
      <Button
        className={classes.menuButton}
        onClick={() => {
          console.log("User wants to sign in");
        }}
      >
        <Typography variant="body2" color="inherit">
          Sign in
        </Typography>
      </Button>
    </Link>
  );

  return (
    <>
      <Hidden xsDown>
        <AppBar className={classes.header} color="primary" position="static">
          <Toolbar className={classes.toolbar} variant="dense" style={{ justifyContent: "space-between" }}>
            <div className={classes.links}>
              <Link to="/">
                <img className={classes.logo} src="/eval/images/logo.svg" alt="NDFP Logo" />
              </Link>

              <NavLinks orientation="header" />
            </div>

            <div className={classes.links}>
                <Link to="/help">
                  <IconButton className={classes.helpButton}>
                    <HelpIcon />
                  </IconButton>
                </Link>
              {props.userId !== null ? signOutButton : signInButton}
            </div>
          </Toolbar>
        </AppBar>
      </Hidden>

      {!evalID && !followUpID && props.userId && (
        <Hidden smUp>
          <AppBar position="relative">
            <Toolbar>
              <IconButton onClick={handleDrawerToggle}>
                <MenuIcon color="inherit" className={classes.menuButton} />
              </IconButton>

              <Route path="/home" render={() => (<Typography variant="h6">Home</Typography>)} />
              <Route path="/evals" render={() => (<Typography variant="h6">Evaluations</Typography>)} />
              <Route path="/follow-ups" render={() => (<Typography variant="h6">Follow-Ups</Typography>)} />
            </Toolbar>
          </AppBar>

          <Drawer
            className={classes.drawer}
            variant="temporary"
            classes={{ paper: classes.paper }}
            anchor="left"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <div className={classes.drawerRoot}>
              <div className={classes.drawerHeader}>
                <div className={classes.greeting}>
                  <Typography className={classes.greetingText} variant="h6">
                    Welcome,
                  </Typography>
                  <Typography className={classes.greetingText} variant="body1">
                    {name}
                  </Typography>
                </div>
                <NavLinks orientation="drawer" />
              </div>
              <div className={classes.drawerFooter}>
                <Link to="/help" className={classes.linkText}>
                <Button className={classes.buttonLink} color="inherit">
                  <HelpIcon />
                  <Typography className={classes.buttonLinkText} variant="body1">
                    Help
                  </Typography>
                </Button>
                </Link>
                <div className={classes.signOutButtonFooter}>{signOutButton}</div>
              </div>
            </div>
          </Drawer>
        </Hidden>
      )}
    </>
  );
};

export default AppHeader;
